import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import ReportTemplateView from "./ReportTemplateView.jsx";
import { ReportTemplateContext } from "./ReportTemplateContext.js";
import { Riskidataa } from "../../../assets/Riskidata.js";
import { normalizeType } from "../../utils/normalizeType.js";
import { blockRegistry } from "../../utils/blockUtils.js";
import { mergeProperties } from "../../utils/mergeProperties.js";
import ToastMessage from "../../ToastMessage.jsx";
import { unstable_batchedUpdates } from 'react-dom';
import useDocumentTitle from "../../hooks/useDocumentTitle";
// import { getKiinteistoImageById } from "../../api/api";


import {
  getKiinteistoWhole,
  getReportsByKiinteisto,
  createReportFromTemplateType,
  getTemplateByType,
  getReportById,
  getRiskTableByKiinteisto,
  updateReportSection,
  createSectionForReport,
  getAllUsers,
  getReportUsers,
  updateReportUsers,
  getKiinteistoImagesByKiinteistoId
} from "../../../api/api.js";
import { log } from "../../utils/generalConsoleLogger.js";

// Utility for safe API calls
const safeApiCall = async (apiFunc, ...args) => {
  try {
    const res = await apiFunc(...args);
    if (!res || !res.data) throw new Error("No data returned");
    return { data: res.data, error: null, status: res.status || 200 };
  } catch (err) {
    return { data: null, error: err, status: err.response?.status || 500 };
  }
};

export const ReportTemplateContainer = () => {

  const { id: kiinteistoId, templateName, reportId, sectionId, section:sectionType, subsectionId: subsectionIdParam } = useParams();

  const [activeSectionId, setActiveSectionId] = useState(sectionId || null);
  const [activeSubsectionId, setActiveSubsectionId] = useState(subsectionIdParam ?? null);

  useEffect(() => {
    if (sectionId) setActiveSectionId(sectionId);
  }, [sectionId]);

  useEffect(() => {
    setActiveSubsectionId(subsectionIdParam ?? null);
  }, [subsectionIdParam]);

  // ----- State -----
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [report, setReport] = useState(null);
  const [sections, setSections] = useState([]);

  const [rakennusData, setRakennusData] = useState(null);
  const [mergedRakennusData, setMergedRakennusData] = useState(null);

  // kiinteistoId present -> a real report for that kiinteistö (tab title
  // shows its address + report type); no kiinteistoId -> the template-editing
  // view itself (no address to show, so just the template's own name).
  useDocumentTitle(
    kiinteistoId
      ? (rakennusData
          ? `${rakennusData.rakennukset_fulls?.[0]?.osoite || rakennusData.kohteen_nimi || 'Kiinteistö'} – ${templateName}`
          : undefined)
      : (templateName ? `${templateName} raporttipohja` : undefined)
  );

  const [editMode, setEditMode] = useState(() => {
    try { return localStorage.getItem('ktp_edit_mode') === 'true'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('ktp_edit_mode', editMode ? 'true' : 'false'); } catch {}
  }, [editMode]);
  const [riskidata, setRiskidata] = useState(Riskidataa);

  const [isTemplateView, setIsTemplateView] = useState(!kiinteistoId);

  const [lockedSections, setLockedSections] = useState({});

  const [popup, setPopup] = useState(null); 

  const [versionKey, setVersionKey] = useState(0);
  const bumpVersionKey = useCallback(() => setVersionKey(k => k + 1), []);



  // Remove auto-dismiss
  const showPopup = useCallback((popupObj) => {
    setPopup(popupObj);
  }, []);

  // Clear popup when active section changes
  useEffect(() => {
    setPopup(null);
  }, [activeSectionId]);

  // ------------------------------------------------------------
  // ✅ Block Editing State
  // ------------------------------------------------------------
  const activeSection = useMemo(
    () => sections.find((s) => String(s.id) === String(activeSectionId)) || null,
    [sections, activeSectionId]
  );

  const lastSavedRef = useRef("");

  
  // Commented out 26/03 since activeSection.content really does not tell last saved content.
  // useEffect(() => {
  //   if (!activeSection?.content) return;

  //   lastSavedRef.current = activeSection.content;
  // }, [activeSectionId]);


  // Current editor JSON content
const ensureBlockIds = (node, isListItem = false) => {
  if (!node || typeof node !== "object") return node;

  if (Array.isArray(node)) {
    // Pass isListItem=true for each element of an "items" array
    // — caller sets this via the key check below
    return node.map((item) => ensureBlockIds(item, isListItem));
  }

  const withId = node.constructor === Object ? { ...node } : node;

  // Only assign id if this is a real block (has "type") or a list item
  const shouldHaveId = !!withId.type || isListItem;

  if (shouldHaveId && !withId.id) {
    withId.id = crypto.randomUUID();
  }

  // Recurse into all object/array values
  for (const key of Object.keys(withId)) {
    const value = withId[key];
    if (value && typeof value === "object") {
      // Flag children of "items" arrays as list items
      const childIsListItem = key === "items";
      withId[key] = Array.isArray(value)
        ? value.map((item) => ensureBlockIds(item, childIsListItem))
        : ensureBlockIds(value, false);
    }
  }

  return withId;
};

  const content = useMemo(() => {
    try {
      // console.log("📦 RAW activeSection.content:", activeSection?.content);

      const parsed = activeSection?.content
        ? JSON.parse(activeSection.content)
        : { blocks: [] };


      const normalized = ensureBlockIds(parsed);


      return normalized;
    } catch (err) {
      console.error("❌ CONTENT PARSE FAILED:", err);

      return { blocks: [] };
    }
  }, [activeSection?.content]);

  // ------------------------------------------------------------
// ✅ Find nearest parent subsection title from blockPath
// ------------------------------------------------------------
  const getParentSubsectionTitle = useCallback(
    (blockPath) => {
      let currentBlocks = content.blocks;
      let parentTitle = "";

      for (let depth = 0; depth < blockPath.length; depth++) {
        const index = blockPath[depth];
        const block = currentBlocks[index];

        if (!block) break;

        // If this is a subsection, update title
        if (block.type === "subsection" || block.type === "subsection2") {
          parentTitle = block.data?.title || "";
          currentBlocks = block.data?.blocks || [];
        } else {
          break;
        }
      }

      return parentTitle;
    },
    [content.blocks]
  );

    // Sync content whenever activeSection changes
  // useEffect(() => {
  //   if (!activeSection) return;

  //   try {
  //     const parsed = activeSection.content
  //       ? JSON.parse(activeSection.content)
  //       : { blocks: [] };

  //     // normalize in case content is just a number / array / string
  //     setContent(parsed.blocks ? parsed : { blocks: [] });
  //   } catch (err) {
  //     console.error("Failed to parse section content:", err);
  //     setContent({ blocks: [] });
  //   }
  // }, [activeSection]);

  // useEffect(() => {
  //   if (!activeSection) return;

  //   try {
  //     const parsed = activeSection.content
  //       ? JSON.parse(activeSection.content)
  //       : { blocks: [] };

  //     setContent(parsed);
  //   } catch (err) {
  //     setContent({ blocks: [] });
  //   }
  // }, [activeSection?.id]);

  // Autosave timer ref
  const saveTimeoutRef = useRef(null);
  // Always holds the most recent derived content so debounced saves don't use stale metadata
  const latestContentRef = useRef(null);
  latestContentRef.current = content;

  const setSectionsAndReport = useCallback((newSections) => {
    setSections(newSections);
    setReport(prev => prev ? { ...prev, report_sections: newSections } : prev);
  }, []);

  // ------------------------------------------------------------
  // ✅ Fetch riskidata
  // ------------------------------------------------------------
  const fetchRiskidata = useCallback(async () => {
    if (!kiinteistoId) return Riskidataa;

    try {
      const res = await getRiskTableByKiinteisto(kiinteistoId);
      const table = res.data?.[0];

      if (!table?.data) return Riskidataa;

      const formatted = Object.entries(table.data).flatMap(
        ([category, items]) =>
          items.map((item) => ({
            category,
            label: item.item,
            risk: item.risk,
            description: item.notes || "",
          }))
      );

      unstable_batchedUpdates(() => {
        setRiskidata(formatted);
      });
      return formatted;
    } catch (err) {
      console.error("Failed to fetch riskidata:", err);
      return Riskidataa;
    }
  }, [kiinteistoId]);

  // ------------------------------------------------------------
  // ✅ Fetch report/template data
  // ------------------------------------------------------------
  const fetchReportData = useCallback(async () => {
      setLoading(true);
      setError(null);
      log({context: 'fetchReportData'})
      try {
        let fetchedReport = null;
        let kiData = null; // ← add this

        if (kiinteistoId) {
          const { data: fetchedKiData, error: kiError } = await safeApiCall(
            getKiinteistoWhole,
            kiinteistoId
          );
          if (kiError) throw kiError;
          kiData = fetchedKiData;

          if (reportId && !isNaN(Number(reportId))) {
            // Load specific report by ID
            const { data: reportData, error: rError } = await safeApiCall(
              getReportById,
              reportId
            );
            if (rError) throw rError;
            fetchedReport = reportData;
          } else {
            // Legacy: find first report of this type, or create one
            const { data: reportsData, error: rError } = await safeApiCall(
              getReportsByKiinteisto,
              kiinteistoId
            );
            if (rError) throw rError;
            fetchedReport = reportsData.find((r) => r.type === templateName);

            if (!fetchedReport) {
              const { data: newReport, error: cError } = await safeApiCall(
                createReportFromTemplateType,
                kiinteistoId,
                templateName
              );
              if (cError) throw cError;
              fetchedReport = newReport;
            }
          }
        } else {
          const { data: templateData, error: tError } = await safeApiCall(
            getTemplateByType,
            templateName
          );
          if (tError) throw tError;
          fetchedReport = templateData;
        }

        // Normalize block IDs: old sections may have blocks with no id, which breaks subsection
        // navigation (sidebar reads raw DB content, renderer uses ensureBlockIds — they diverge).
        // Transform synchronously for immediate correct state, save in background (fire-and-forget).
        const rawSections = fetchedReport?.report_sections || [];
        const normalizedSections = rawSections.map((s) => {
          if (!s.content) return s;
          try {
            const parsed = JSON.parse(s.content);
            const normalized = ensureBlockIds(parsed);
            const normStr = JSON.stringify(normalized);
            if (normStr !== s.content) {
              updateReportSection(s.id, { content: normStr }).catch(() => {});
              return { ...s, content: normStr };
            }
          } catch {}
          return s;
        });

        // ← one batch instead of separate renders
        unstable_batchedUpdates(() => {
          if (kiData) setRakennusData(kiData);
          setReport(fetchedReport);
          setSections(normalizedSections);
        });

      } catch (err) {
        console.error("Failed to fetch report/template:", err);
        setError({ message: err.message });
      } finally {
        setLoading(false);
      }
    }, [kiinteistoId, templateName, reportId, sectionId]);

  // Initial load
  useEffect(() => {
    fetchReportData();
    fetchRiskidata();
  }, [fetchReportData, fetchRiskidata]);

  // ------------------------------------------------------------
  // ✅ Section updater
  // ------------------------------------------------------------
  const updateSection = useCallback((updatedSection) => {
    setSections((prev) =>
      prev.map((s) => (s.id === updatedSection.id ? updatedSection : s))
    );
  }, []);
const updateOtherSectionContent = useCallback(
  async (sectionId, newContent) => {
    if (!sectionId || !newContent) return false;

    const targetSection = sections.find(
      (section) => String(section.id) === String(sectionId)
    );

    if (!targetSection) {
      console.error(
        "❌ Target section not found:",
        sectionId
      );
      return false;
    }

    try {
      const newString =
        typeof newContent === "string"
          ? newContent
          : JSON.stringify(newContent);

      // SAVE TO DATABASE
      await updateReportSection(sectionId, {
        content: newString,
        template: isTemplateView
          ? true
          : targetSection.template,
      });

      // Update local sections
      setSections((prev) =>
        prev.map((section) =>
          String(section.id) === String(sectionId)
            ? {
                ...section,
                content: newString,
              }
            : section
        )
      );

      // Keep report.report_sections synchronized
      setReport((prev) =>
        prev
          ? {
              ...prev,
              report_sections: (
                prev.report_sections ?? []
              ).map((section) =>
                String(section.id) === String(sectionId)
                  ? {
                      ...section,
                      content: newString,
                    }
                  : section
              ),
            }
          : prev
      );

      console.log(
        "✅ Section saved:",
        targetSection.name
      );

      return true;
    } catch (err) {
      console.error(
        "❌ Failed saving section:",
        err.response?.data || err
      );

      return false;
    }
  },
  [sections, isTemplateView]
);
  // ------------------------------------------------------------
  // ✅ Block Editing Logic
  // ------------------------------------------------------------
  const isSectionLocked = useCallback(
    (sectionId = activeSection?.id) => !!lockedSections[sectionId],
    [lockedSections, activeSection]
  );


  //WHOLE SAVE CHAIN LOGIC:  BLOCK-> onChange-> handleBlockChange -> debouncedSave -> handleBlockBlur 

  //Indicator toast for section saves to db.
  const [sectionUpdateToast, setSectionUpdateToast] = useState({ show: false, message: "", bg: "success" });

  // Called when user leaves editor (blur)
const handleBlockBlur = useCallback(
  async (contentToSave, sectionId = activeSection?.id) => {
    log({context:'handleBlockBlur'})
    if (!contentToSave) contentToSave = structuredClone(content);

    if (!activeSection || !report || activeSection.id !== sectionId) {
      setSectionUpdateToast({
        show: true,
        message: "Skipped save: active section changed",
        bg: "warning"
      });
      return;
    }

    try {
      let updatedSections;

      if (!activeSection.id) {
        // CREATE
        const payload = {
          report_id: reportId,
          name: activeSection.name,
          type: activeSection.type,
          content: contentToSave,
          template: isTemplateView,
        };
        const createdResp = await createSectionForReport(payload);
        const createdSection = createdResp.data;
        updatedSections = sections.map((s) =>
          s === activeSection ? createdSection : s
        );

      } else {
        // UPDATE
        const newString = JSON.stringify(contentToSave);
        if (newString === lastSavedRef.current) return;

        try {
          await updateReportSection(sectionId, {
            content: newString,
            template: isTemplateView ? true : activeSection.template,
          });

          lastSavedRef.current = newString;
          setLockedSections((prev) => ({ ...prev, [sectionId]: false }));
          setSectionUpdateToast({
            show: true,
            message: `Kappale päivitettiin onnistuneesti.`,
            bg: "success"
          });

        } catch (err) {
          if (err.response?.status === 423 || err.code === "LOCKED") {
            setLockedSections((prev) => ({ ...prev, [sectionId]: true }));
            showPopup({
              type: "locked",
              message: `Toinen käyttäjä muokkaa kappaletta "${activeSection?.name}". Siirry toiseen kappaleeseen.`,
            });
            return;
          }

          if (!navigator.onLine || err.message === 'Network Error') {
            showPopup({
              type: "error",
              message: "⚠️ Ei internet-yhteyttä. Muutoksia ei tallenneta. Tarkista yhteys ja päivitä sivu.",
            });
            return;
          }

          // Other errors → toast
          let message = "Tallennus epäonnistui";
          if (err.response) {
            message += `: ${err.response.status} ${err.response.statusText}`;
          } else {
            message += `: ${err.message}`;
          }
          setSectionUpdateToast({ show: true, message, bg: "danger" });
          return;
        }

        // Functional updaters avoid the stale-closure bug where sections captured
        // at callback creation time doesn't include chip sections added by fetchReportData().
        setSections(prev => prev.map(s => s.id === sectionId ? { ...s, content: newString } : s));
        setReport(prev => prev ? {
          ...prev,
          report_sections: (prev.report_sections ?? []).map(s => s.id === sectionId ? { ...s, content: newString } : s),
        } : prev);
        return;
      }

      setSectionsAndReport(updatedSections);

    } catch (err) {
      console.error("Unexpected error in handleBlockBlur:", err);
      setSectionUpdateToast({
        show: true,
        message: "Odottamaton virhe tallennuksessa.",
        bg: "danger"
      });
    }
  },
  [activeSection, report, sections, reportId, isTemplateView, content]
);
  //Auto save if user tries leave without saving
  // useEffect(() => {
  //   const handleBeforeUnload = (e) => {
  //     if (!activeSection) return;

  //     // Cancel pending autosave
  //     if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

  //     // Trigger your normal blur save
  //     handleBlockBlur(content, activeSection?.id);

  //     // Optional: show prompt if there are unsaved changes
  //     const contentString = JSON.stringify(content);
  //     if (contentString !== lastSavedRef.current) {
  //       e.preventDefault();
  //       e.returnValue = "";
  //     }
  //   };

  //   window.addEventListener("beforeunload", handleBeforeUnload);
  //   return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  // }, [activeSection, content, handleBlockBlur]);

  const debouncedSave = (newContent) => {
    log({context:'debouncedSave'})
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    const sectionId = activeSection?.id;
    saveTimeoutRef.current = setTimeout(() => {
      // Merge: keep blocks from this text edit, but pull readiness + other metadata
      // from the latest content so concurrent readiness clicks aren't overwritten.
      const latest = latestContentRef.current;
      const toSave = latest
        ? { ...newContent, ...latest, blocks: newContent.blocks }
        : newContent;
      handleBlockBlur(toSave, sectionId);
    }, 500);
  };

    // Fetch latest section from backend whenever activeSectionId changes
  
  // Auto-clear expired locks after 1 minute
useEffect(() => {
  const lockedIds = Object.entries(lockedSections)
    .filter(([_, isLocked]) => isLocked)
    .map(([id]) => id);

  if (lockedIds.length === 0) return;

  const timer = setTimeout(() => {
    setLockedSections(prev => {
      const updated = { ...prev };
      lockedIds.forEach(id => { updated[id] = false; });
      return updated;
    });
  }, 60_000); // 1 minute — matches DB lock TTL

  return () => clearTimeout(timer);
}, [lockedSections]);


  const handleBlockChange = useCallback(
    (path, patch, expectedBlockId) => {
      if (!activeSection) return;

      log({context:'handleBlockChange'})

      if (isSectionLocked()) {
        // Revert to last saved content
        const parsedContent = activeSection.content
          ? JSON.parse(activeSection.content)
          : { blocks: [] };
        // setContent(parsedContent);

        // Optional: show popup
      showPopup({
        type: "locked",
        message: `Toinen käyttäjä on muokkaamassa kappaletta "${activeSection?.name}". Siirry toiseen kappaleeseen.`
      });

        return;
      }

      const newContent = structuredClone(content);
        let blocks = newContent.blocks;
        for (let i = 0; i < path.length - 1; i++) {
          if (!blocks[path[i]]?.data?.blocks) return;
          blocks = blocks[path[i]].data.blocks;
        }
        const index = path[path.length - 1];
        if (!blocks[index]) return;

      // Hard guard: if the block at this path doesn't match the expected block ID,
      // we're about to apply a patch to the wrong block (e.g. pendingContentRef
      // contamination from a different section). Refuse and log loudly.
      if (expectedBlockId && blocks[index].id && blocks[index].id !== expectedBlockId) {
        console.error(
          '[handleBlockChange] BLOCK ID MISMATCH — refusing save to prevent cross-section contamination.',
          'path:', path, 'expected id:', expectedBlockId, 'actual id:', blocks[index].id
        );
        return;
      }

      blocks[index].data = { ...blocks[index].data, ...patch };

      // setContent(newContent);

      // only autosave
    debouncedSave(newContent);
    },

    [content, sections, activeSection, debouncedSave, setSectionsAndReport, isSectionLocked, showPopup]
  );


  function gatherToimenpideItems(blocks = [], sectionMeta = {}) {
    if (!Array.isArray(blocks)) return [];

    const items = [];

    for (const block of blocks) {
      if (!block || typeof block !== "object") continue;

      // 1️⃣ Direct ToimenpideEhdotukset block
      if (block.type === "ToimenpideEhdotukset") {
        const blockItems = block.data?.items;
        if (!Array.isArray(blockItems)) continue;

        for (const item of blockItems) {
          if (!item?.text?.trim()) continue;

          const immediate = !!item.immediate;
          const significant = !!item.significant; // ✅ keep independent
          const tutkimus = !!item.tutkimus;

          const regularToimenpide = item.immediate===false && item.significant===false

          // Ignore completely empty rows
          // if (item.text.trim().length < 0) continue;

          items.push({
            id: item.id,
            text: item.text.trim(),

            // origin info (VERY important)
            sectionId: sectionMeta.sectionId,
            sectionName: sectionMeta.sectionName,

            category: item.ptsCategory || "Muu",
            subCategory: item.ptsSection,

            immediate : immediate,
            significant : significant,
            regular : regularToimenpide,
            tutkimus,

            // derived flags (clean separation)
            includeInPTS: item.includeInPTS,
            affectsYhteenveto: significant || immediate,
          });
        }
      }

      // 2️⃣ Recursive nested blocks
      const nested = block.data?.blocks;
      if (Array.isArray(nested) && nested.length > 0) {
        items.push(
          ...gatherToimenpideItems(nested, sectionMeta)
        );
      }
    }

    return items;
  }

  const extractPropertyIdsFromReport = (sections = []) => {
  for (const section of sections) {
    try {
      const content = typeof section.content === "string"
        ? JSON.parse(section.content)
        : section.content || { blocks: [] };

      const blocks = content.blocks || [];

      for (const block of blocks) {
        if (block?.type === "propertyInfo" && Array.isArray(block.data?.propertyIds)) {
          return block.data.propertyIds.filter(Boolean);
        }
      }
    } catch (err) {
      console.warn("Failed to parse section content for property IDs:", err);
    }
  }
  return [];
};


const previousPropertyIdsRef = useRef([]);

useEffect(() => {
  const propertyIds = extractPropertyIdsFromReport(sections);

  // ✅ Skip if no properties
  if (!propertyIds.length) {
    setMergedRakennusData(null);
    previousPropertyIdsRef.current = [];
    return;
  }

  // ✅ Skip if property IDs are exactly the same as last time
  const idsChanged =
    propertyIds.length !== previousPropertyIdsRef.current.length ||
    propertyIds.some((id, i) => id !== previousPropertyIdsRef.current[i]);

  if (!idsChanged) return;

  previousPropertyIdsRef.current = propertyIds;

  const fetchAndMergeProperties = async () => {
    log({context:'fetchAndMergeProperties', message:'APICALL'})
    try {
      const results = await Promise.all(
        propertyIds.map((id) => getKiinteistoWhole(id).then((res) => res.data))
      );

      // Ensure main property first
      const mainId = propertyIds[0];
      results.sort((a, b) =>
        a.id_kiinteisto === mainId ? -1 : b.id_kiinteisto === mainId ? 1 : 0
      );

      const mergedCard = mergeProperties([rakennusData, ...results]);
      setMergedRakennusData(mergedCard);
    } catch (err) {
      console.error("Failed to fetch or merge properties:", err);
      setMergedRakennusData(null);
    }
  };

  fetchAndMergeProperties();
}, [sections, rakennusData]);


// const fetchAndMergeProperties = useCallback(async () => {
//   const propertyIds = extractPropertyIdsFromReport(sections);

//   if (!propertyIds.length) {
//     setMergedRakennusData(null);
//     return;
//   }

//   log({ context: "fetchAndMergeProperties", message: "Callback triggered" });

//   try {
//     const results = await Promise.all(
//       propertyIds.map((id) => getKiinteistoWhole(id).then((res) => res.data))
//     );

//     // Ensure main property first
//     const mainId = propertyIds[0];
//     results.sort((a, b) =>
//       a.id_kiinteisto === mainId ? -1 : b.id_kiinteisto === mainId ? 1 : 0
//     );

//     const mergedCard = mergeProperties([rakennusData, ...results]);

//     // Only update state if actually changed
//     setMergedRakennusData((prev) => {
//       if (JSON.stringify(prev) !== JSON.stringify(mergedCard)) return mergedCard;
//       return prev;
//     });
//   } catch (err) {
//     console.error("Failed to fetch or merge properties:", err);
//     setMergedRakennusData(null);
//   }
// }, [rakennusData, sections]);

// const fetchRef = useRef(fetchAndMergeProperties);

// useEffect(() => {
//   if (fetchRef.current === fetchAndMergeProperties) {
//     console.log("✅ Callback is the same instance as before");
//   } else {
//     console.log("⚠️ Callback is a new function!");
//   }
//   fetchRef.current = fetchAndMergeProperties;
// }, [fetchAndMergeProperties]);

function gatherAllToimenpideItems(sections = []) {
  if (!Array.isArray(sections)) return [];

  const all = [];

  for (const section of sections) {
    if (!section) continue;

    try {
      const parsed =
        typeof section.content === "string"
          ? JSON.parse(section.content)
          : section.content || { blocks: [] };

      const blocks = Array.isArray(parsed?.blocks)
        ? parsed.blocks
        : [];

      all.push(
        ...gatherToimenpideItems(blocks, {
          sectionId: section.id,
          sectionName: section.name,
        })
      );
    } catch (err) {
      console.warn("Invalid section JSON:", err);
    }
  }

  return all;
}




const groupedToimenpiteet = useMemo(() => {
  const gathered = gatherAllToimenpideItems(sections);

  // console.log('gathered', gathered)

  const grouped = {
    immediate: {},
    significant: {},
    regular: {}
  };

  for (const item of gathered) {
    const category = item.category || "Muu";

    if (item.immediate) {
      grouped.immediate[category] ||= [];
      if (!grouped.immediate[category].some(i => i.text === item.text)) {
        grouped.immediate[category].push(item);
      }
    }

    if (item.significant) {
      grouped.significant[category] ||= [];
      if (!grouped.significant[category].some(i => i.text === item.text)) {
        grouped.significant[category].push(item);
      }
    }

    if (!item.significant && !item.immediate) {
       grouped.regular[category] ||= [];
      if (!grouped.regular[category].some(i => i.text === item.text)) {
        grouped.regular[category].push(item);
      }
    }

    // 🔹 ignore tutkimus items entirely
  }

  return grouped;
}, [sections]);




  const renameSection = useCallback(
    async (sectionId, newName) => {
      if (!sectionId || !newName.trim()) return;

      const trimmed = newName.trim();
      const newType = normalizeType(trimmed);

      try {
        // ✅ Backend update
        await updateReportSection(sectionId, {
          name: trimmed,
          type: newType,
        });

        // ✅ Local update
        setSections((prev) =>
          prev.map((s) =>
            s.id === sectionId
              ? { ...s, name: trimmed, type: newType }
              : s
          )
        );

        console.log("✅ Section renamed:", trimmed);
      } catch (err) {
        console.error("❌ Failed to rename section:", err);
        throw err;
      }
    },
    []
  );


  const updateSectionFromTemplate = useCallback(
    async (availablePTSSections) => {
      try {
        const templateResp = await getTemplateByType(templateName);
        const templateReport = templateResp.data;
        if (!templateReport?.report_sections) {
          alert("Mallia ei löytynyt tälle osiolle.");
          return;
        }

        const templateSection = templateReport.report_sections.find(s => s.type === sectionType);
        if (!templateSection?.content) {
          alert("Malliosiota ei löytynyt.");
          return;
        }

        const parsed = JSON.parse(templateSection.content);

        function rehydrate(blocks) {
          return blocks.map((b) => {
            if (b.type?.startsWith("PtsBlock")) {
              return {
                ...b,
                data: {
                  ...b.data,
                  availablePTSSections,
                  propertyId: rakennusData?.id_kiinteisto,
                  kiinteistotunnus: rakennusData?.kiinteistotunnus,
                },
              };
            }
            if (b.type === "subsection") {
              return { ...b, data: { ...b.data, blocks: rehydrate(b.data.blocks) } };
            }
            return b;
          });
        }

        parsed.blocks = rehydrate(parsed.blocks);

        // setContent(parsed);
        await handleBlockBlur(parsed);

        alert("Osio päivitetty mallista ja PTS-tiedot lisätty.");
      } catch (err) {
        console.error("Failed to update from template:", err);
        alert("Osion päivitys mallista epäonnistui.");
      }
    },
    [templateName, sectionType, rakennusData, handleBlockBlur]
  );

  const handleAddBlock = useCallback(
    (type, path = []) => {
      const newBlock =
        type === "propertyInfo"
          ? { type, name: blockRegistry[type]?.label || type, data: { useCustomProperty: false, propertyId: null } }
          : type === "subsection"
          ? { type, name: "Uusi aliosio", data: { title: "Uusi aliosio", blocks: [] } }
          : type === "subsection2"
          ? { type, name: "Aliosio (bold header)", data: { title: "Uusi otsikko", blocks: [] } }
          : { type, name: blockRegistry[type]?.label || type, data: { value: "", showPart: blockRegistry[type]?.showPart || "all" } };

      const newContent = structuredClone(content);
      let blocks = newContent.blocks;
      for (let i = 0; i < path.length; i++) blocks = blocks[path[i]].data.blocks;
      blocks.push(newBlock);

      // setContent(newContent);
      handleBlockBlur(newContent); // autosave
    },
    [content, handleBlockBlur]
  );

  const handleDeleteBlock = useCallback(
    (path) => {
      if (!window.confirm("Haluatko varmasti poistaa tämän lohkon?")) return;

      const newContent = structuredClone(content);
      let blocks = newContent.blocks;
      for (let i = 0; i < path.length - 1; i++) blocks = blocks[path[i]].data.blocks;
      blocks.splice(path[path.length - 1], 1);

      // setContent(newContent);
      handleBlockBlur(newContent); // autosave
    },
    [content, handleBlockBlur]
  );

  const moveBlock = useCallback(
    (path, direction) => {
      const newContent = structuredClone(content);
      let blocks = newContent.blocks;
      for (let i = 0; i < path.length - 1; i++) blocks = blocks[path[i]].data.blocks;

      const idx = path[path.length - 1];
      const swapIdx = direction === "up" ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= blocks.length) return;

      [blocks[idx], blocks[swapIdx]] = [blocks[swapIdx], blocks[idx]];

      // setContent(newContent);
      handleBlockBlur(newContent); // autosave
    },
    [content, handleBlockBlur]
  );

//Report users functions
// ------------------------------------------------------------
// ✅ Report User Assignment State
// ------------------------------------------------------------
const [allUsers, setAllUsers] = useState([]);

const [assignments, setAssignments] = useState({
  koordinaattori: [],
  kuntotutkija: [],
  rakennustekniikka: [],
  lvia: [],
  sahko: [],
});

const [usersLoading, setUsersLoading] = useState(true);
const [hasUserChanges, setHasUserChanges] = useState(false);

const userInitialLoad = useRef(true);


// ------------------------------------------------------------
// ✅ Fetch report users + assignments
// ------------------------------------------------------------
useEffect(() => {
  if (!reportId) return;
  const loadReportUsers = async () => {
    try {
      setUsersLoading(true);
      const [usersRes, assignedRes] = await Promise.all([
        getAllUsers(),
        getReportUsers(reportId),
      ]);
      const all = usersRes.data;
      const assigned = assignedRes.data;

      const normalized = assigned.map((u) => {
        const fullUser = all.find((au) => au.id === u.id);
        return {
          ...u,
          ...fullUser,
          uniqueId: `${u.id}-${u.report_users?.system}`,
        };
      });

      unstable_batchedUpdates(() => {
        setAllUsers(all);
        setAssignments({
          koordinaattori: normalized.filter((u) => u.report_users?.system === "koordinaattori"),
          kuntotutkija: normalized.filter((u) => u.report_users?.system === "kuntotutkija"),
          rakennustekniikka: normalized.filter((u) => u.report_users?.system === "rakennustekniikka"),
          lvia: normalized.filter((u) => u.report_users?.system === "lvia"),
          sahko: normalized.filter((u) => u.report_users?.system === "sahko"),
        });
      });

      userInitialLoad.current = false;
    } catch (err) {
      console.error("❌ Failed to load report users:", err);
    } finally {
      setUsersLoading(false);
    }
  };
  loadReportUsers();
}, [reportId]);

// ------------------------------------------------------------
// ✅ Add user to system
// ------------------------------------------------------------
const addUserToSystem = useCallback((system, userId) => {
  if (!userId) return;

  const found = allUsers.find((u) => u.id === parseInt(userId));
  if (!found) return;

  // prevent duplicates
  if ((assignments[system] || []).some((u) => u.id === found.id)) return;

  setAssignments((prev) => ({
    ...prev,
    [system]: [
      ...(prev[system] || []),
      {
        ...found,
        report_users: { role: "tarkastaja", system },
        uniqueId: `${found.id}-${system}`,
      },
    ],
  }));

  setHasUserChanges(true);
}, [allUsers, assignments]);

// ------------------------------------------------------------
// ✅ Remove user
// ------------------------------------------------------------
const removeUserFromSystem = useCallback((system, userId) => {
  setAssignments((prev) => ({
    ...prev,
    [system]: (prev[system] || []).filter((u) => u.id !== userId),
  }));

  setHasUserChanges(true);
}, []);

// ------------------------------------------------------------
// ✅ Autosave report user assignments
// ------------------------------------------------------------
useEffect(() => {
  if (usersLoading) return;
  if (userInitialLoad.current) return;
  if (!hasUserChanges) return;

  const timeout = setTimeout(async () => {
    try {
      const body = Object.entries(assignments).flatMap(
        ([system, list]) =>
          list.map((u) => ({
            id_user: u.id,
            role: u.report_users?.role || "tarkastaja",
            system,
          }))
      );

      await updateReportUsers(reportId, body);

      console.log("✅ Report users saved");
      setHasUserChanges(false);
    } catch (err) {
      console.error("❌ Failed saving report users:", err);
    }
  }, 800);

  return () => clearTimeout(timeout);
}, [assignments]);


const SKIP_SECTION_TYPES = [
  "allekirjoitukset",
  "kansisivu",
  "kohteen_perustiedot",
  "johdanto"
];
const FULL_TEXT_SECTIONS = new Set([
  "yhteenveto",
]);

const extractMeaningfulReportData = useCallback(async (sectionsToExtract = sections) => {
  const result = [];

  // --- Property data: deduplicate buildings ---
  const sourceData = (mergedRakennusData && Object.keys(mergedRakennusData).length > 0)
    ? mergedRakennusData : rakennusData;

  if (sourceData) {
    const allBuildings = (sourceData.rakennukset_fulls || []).filter(r => r.isMainBuilding);

    const seen = new Map();
    for (const r of allBuildings) {
      const key = [
        r.rakennusvuosi, r.kokonaisala, r.kerrosala, r.huoneistoala,
        r.runkotapa, r.julkisivumateriaali, r.rakennusaine,
        r.lammitystapa, r.lammitysenergialahde, r.kerroksia,
      ].join("|");
      if (!seen.has(key)) {
        seen.set(key, { count: 1, data: r });
      } else {
        seen.get(key).count++;
      }
    }

    const buildings = [...seen.values()].map(({ count, data: r }) => {
      const b = {
        count,
        rakennusvuosi: r.rakennusvuosi,
        kokonaisala: r.kokonaisala ? `${r.kokonaisala} m²` : undefined,
        kerrosala: r.kerrosala ? `${r.kerrosala} m²` : undefined,
        huoneistoala: r.huoneistoala ? `${r.huoneistoala} m²` : undefined,
        tilavuus: r.tilavuus ? `${r.tilavuus} m³` : undefined,
        kerroksia: r.kerroksia,
        rakennusluokitus: r.rakennusluokitus,
      };
      return Object.fromEntries(Object.entries(b).filter(([_, v]) => v != null));
    });

    result.push({
      sectionType: "propertyData",
      tontinKoko: sourceData.tontin_koko ? `${Math.round(sourceData.tontin_koko)} m²` : undefined,
      rakennustenLkm: sourceData.rakennusten_lkm,
      huoneistojenLkm: sourceData.huoneistojen_lkm,
      rakennukset: buildings,
    });
  }

  // --- Sections: texts (for LLM style teaching) + toimenpiteet ---
  const TRUNCATE_AT = 600;

  const isMultiSentence = (text) => {
    const sentences = text.trim()
      .split(/\.(?:\s+|\n+|$)/)
      .map(s => s.trim())
      .filter(Boolean);
    return sentences.length > 1;
  };

  const prepareText = (raw, isFullText = false) => {
    if (isFullText) return raw.trim();
    const paragraphs = raw.split(/\n\n+/);
    const meaningful = paragraphs.slice(0, 2).join('\n\n').trim();
    return meaningful.length > TRUNCATE_AT
      ? meaningful.slice(0, TRUNCATE_AT).trimEnd() + "…"
      : meaningful;
  };

  for (const section of sectionsToExtract) {
    if (!section?.content) continue;
    if (SKIP_SECTION_TYPES.includes(section.type)) continue;

    try {
      const parsed = typeof section.content === "string"
        ? JSON.parse(section.content) : section.content;
      const blocks = parsed?.blocks || [];

      const isFullText = FULL_TEXT_SECTIONS.has(section.type);

      const sectionData = {
        sectionType: section.type,
        subsections: [],
        topLevelTexts: [],
        toimenpiteet: [],
      };

      const extractBlocks = (blockList) => {
        for (const block of blockList) {
          if (!block) continue;

          if (block.type === "subsection" || block.type === "subsection2") {
            const sub = {
              title: block.data?.title || "",
              texts: [],
              toimenpiteet: [],
            };

            for (const inner of block.data?.blocks || []) {
              if (inner.type === "LexicalTextarea") {
                const raw = inner.data?.value?.trim();
                if (raw && isMultiSentence(raw)) {
                  sub.texts.push(prepareText(raw, isFullText));
                }
              }
              if (inner.type === "ToimenpideEhdotukset") {
                for (const item of inner.data?.items || []) {
                  if (!item.text?.trim()) continue;
                  if (item.ptsSection === "__NONE__") continue;
                  const t = { text: item.text.trim() };
                  if (item.immediate)   t.immediate = true;
                  if (item.significant) t.significant = true;
                  if (item.tutkimus)    t.tutkimus = true;
                  if (item.ptsCategory) t.category = item.ptsCategory;
                  if (item.ptsSection)  t.section = item.ptsSection;
                  sub.toimenpiteet.push(t);
                }
              }
            }

            if (sub.texts.length || sub.toimenpiteet.length) {
              sectionData.subsections.push(sub);
            }
            continue;
          }

          if (block.type === "LexicalTextarea") {
            const raw = block.data?.value?.trim();
            if (raw && isMultiSentence(raw)) {
              sectionData.topLevelTexts.push(prepareText(raw, isFullText));
            }
          }

          if (block.type === "ToimenpideEhdotukset") {
            for (const item of block.data?.items || []) {
              if (!item.text?.trim()) continue;
              if (item.ptsSection === "__NONE__") continue;
              const t = { text: item.text.trim() };
              if (item.immediate)   t.immediate = true;
              if (item.significant) t.significant = true;
              if (item.tutkimus)    t.tutkimus = true;
              if (item.ptsCategory) t.category = item.ptsCategory;
              if (item.ptsSection)  t.section = item.ptsSection;
              sectionData.toimenpiteet.push(t);
            }
          }
        }
      };

      extractBlocks(blocks);

      const hasContent =
        sectionData.topLevelTexts.length ||
        sectionData.toimenpiteet.length ||
        sectionData.subsections.length;

      if (hasContent) result.push(sectionData);

    } catch (err) {
      console.warn("Failed to extract section:", section.name, err);
    }
  }

  // --- Risk data: skip risk 0 ---
  if (riskidata?.length) {
    const groupedRisks = {};
    for (const item of riskidata) {
      if (item.risk === 0) continue;
      const cat = item.category || "Muu";
      if (!groupedRisks[cat]) groupedRisks[cat] = [];
      const entry = { label: item.label, risk: item.risk };
      if (item.description) entry.description = item.description;
      groupedRisks[cat].push(entry);
    }
    if (Object.keys(groupedRisks).length) {
      result.push({ sectionType: "riskidata", riskGroups: groupedRisks });
    }
  }

  // --- Image metadata ---
  // if (kiinteistoId) {
  //   try {
  //     const res = await getKiinteistoImagesByKiinteistoId(kiinteistoId);
  //     const rows = res.data?.rows || [];

  //     const meaningful = rows.filter(img =>
  //       img.metadata?.description?.trim() || img.category_normalized
  //     );

  //     if (meaningful.length) {
  //       const imagesByCategory = {};
  //       for (const img of meaningful) {
  //         const cat = img.category_normalized || "Muu";
  //         if (!imagesByCategory[cat]) imagesByCategory[cat] = [];
  //         const entry = {};
  //         if (img.subCategory_normalized) entry.subCategory = img.subCategory_normalized;
  //         if (img.metadata?.description?.trim()) entry.description = img.metadata.description.trim();
  //         imagesByCategory[cat].push(entry);
  //       }
  //       result.push({ sectionType: "imageObservations", imagesByCategory });
  //     }
  //   } catch (err) {
  //     console.warn("Failed to fetch image metadata:", err);
  //   }
  // }

  return result;
}, [sections, riskidata, rakennusData, mergedRakennusData, kiinteistoId]);

  // ------------------------------------------------------------
  // ✅ Context value
  // ------------------------------------------------------------
  const contextValue = useMemo(
    () => ({
      report,
      sections,
      updateSection,
      updateOtherSectionContent,
      editMode,
      setEditMode,
      content,
      // setContent,
      handleBlockChange,
      handleBlockBlur,
      renameSection,
      handleAddBlock,
      handleDeleteBlock,
      moveBlock,
      riskidata,
      fetchRiskidata,
      rakennusData,
      mergedRakennusData,
      setMergedRakennusData,
      fetchReportData,
      updateSectionFromTemplate, // ✅ include if you want it accessible
      activeSection,
      activeSectionId,
      activeSubsectionId,
      setActiveSubsectionId,
      allUsers,
      assignments,
      usersLoading,
      addUserToSystem,
      removeUserFromSystem,
      lockedSections,
      popup,
      showPopup,
      groupedToimenpiteet,
      getParentSubsectionTitle,
      versionKey,
      bumpVersionKey,
      extractMeaningfulReportData,
      showToast: setSectionUpdateToast,


    }),
    [
      report,
      sections,
      editMode,
      content,
      riskidata,
      rakennusData,
      mergedRakennusData,
      activeSection,
      updateSection,
      fetchReportData,
      fetchRiskidata,
      handleBlockChange,
      handleBlockBlur,
      handleAddBlock,
      handleDeleteBlock,
      activeSectionId,
      activeSubsectionId,
      moveBlock,
      renameSection,
      updateSectionFromTemplate,
      lockedSections,
      popup,
      groupedToimenpiteet,
      getParentSubsectionTitle,
      versionKey,
      bumpVersionKey,
      extractMeaningfulReportData,
      setSectionUpdateToast,


    ]
  );

  return (
    <ReportTemplateContext.Provider value={contextValue}>
      <ReportTemplateView loading={loading} error={error} />
      <ToastMessage show={sectionUpdateToast.show} message={sectionUpdateToast.message} bg={sectionUpdateToast.bg} onClose={()=>setSectionUpdateToast({ show: false, message: "", bg: "success" })}></ToastMessage>
    </ReportTemplateContext.Provider>
  );
};
